import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { QK } from "@/constants";
import { RealtimeEvent, closeSocket, getSocket, type AdminNotification } from "@/lib/realtime";
import { useAdminNotificationsStore, useSessionStore } from "@/store";

/**
 * Kesh kalitlari: xabar turi → nima eskirdi.
 *
 * Xabar kelganda ochiq turgan ro'yxat O'ZI yangilanishi kerak. Busiz
 * moderator qo'ng'iroqchada "yangi ariza" degan xabarni ko'rar, undan
 * pastdagi jadval esa eski holicha turardi — va u sahifani qo'lda
 * yangilashga majbur bo'lardi.
 */
const INVALIDATES: Record<AdminNotification["type"], readonly (readonly unknown[])[]> = {
  host_application_submitted: [QK.hostApplications.all, QK.dashboard.queue],
  report_submitted: [QK.reports.all, QK.dashboard.queue],
  // `QK.withdrawals` va `QK.payments` — funksiyalar (filtr bilan
  // kalit yasaydi), shuning uchun bu yerda prefiks turadi: React Query
  // prefiks bo'yicha barcha filtrlarni birdan eskirtiradi.
  withdrawal_created: [["withdrawals"]],
  payment_succeeded: [["payments"]],
};

/**
 * Socket ulanishi va navbat xabarlari.
 *
 * FAQAT KIRGAN ODAM UCHUN ulanadi: kirish sahifasida tinglanadigan
 * hodisa yo'q, va cookie'siz ulanish baribir darhol uziladi.
 *
 * XONALARNI SERVER TANLAYDI. Klient "meni mezbon arizalari xonasiga
 * qo'sh" deb ayta olmaydi — backend qo'l berish paytida odamning
 * ruxsatlarini bazadan o'qiydi va xonalarni o'zi ochadi. Ya'ni
 * `payments.read` ruxsati yo'q moderatorga to'lov xabari umuman
 * yetib bormaydi, va buni panelning o'zida tekshirishga hojat yo'q.
 *
 * Har bir hodisa uchta ish qiladi: keshni eskirtiradi, ro'yxatga
 * tushadi va bir marta toast ko'rsatadi. Faqat birinchisi bo'lsa
 * moderator o'zgarishni sezmasdi; faqat oxirgisi bo'lsa toast
 * yopilgach xabar yo'qolardi.
 */
export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const user = useSessionStore(state => state.user);
  const push = useAdminNotificationsStore(state => state.push);
  const clear = useAdminNotificationsStore(state => state.clear);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) {
      closeSocket();
      // Chiqqan odamning navbat xabarlari ekranda qolmasligi kerak:
      // keyingi kirgan odamning ruxsatlari boshqa bo'lishi mumkin.
      clear();
      return;
    }

    const socket = getSocket();
    socket.connect();

    const onAdminNotification = (notification: AdminNotification) => {
      push(notification);

      for (const key of INVALIDATES[notification.type] ?? []) {
        void queryClient.invalidateQueries({ queryKey: key });
      }

      // `id` — takrorlanishga qarshi: qayta ulanishda bir xil xabar
      // ikki marta kelsa, ekranda bitta toast qoladi.
      toast(t(`adminNotifications.types.${notification.type}`), { id: notification.id });
    };

    const onUnauthorized = () => {
      // Sessiya tugadi. Toast yo'q: birinchi HTTP so'rovi moderatorni
      // baribir kirish sahifasiga olib chiqadi, ikkita xabar esa bir
      // narsani ikki marta aytardi.
      void queryClient.invalidateQueries({ queryKey: QK.me });
    };

    socket.on(RealtimeEvent.AdminNotification, onAdminNotification);
    socket.on(RealtimeEvent.Unauthorized, onUnauthorized);

    return () => {
      socket.off(RealtimeEvent.AdminNotification, onAdminNotification);
      socket.off(RealtimeEvent.Unauthorized, onUnauthorized);
    };
  }, [user, queryClient, push, clear, t]);

  return children;
}
